import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Logo } from "@/components/layout/Logo";
import { useBurnSettings } from "@/providers/BurnSettingsProvider";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Lock, AlertTriangle, Check, X, Unlock, DollarSign, Users, RefreshCw, BadgeCheck, Percent, Wallet, ArrowLeftRight, PauseCircle, PlayCircle, Flame, Loader2, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAccount } from 'wagmi';
import { ethers } from 'ethers';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useState, useEffect } from "react";
import { useFirebaseFees } from "@/providers/FirebaseFeeProvider";
import { useWalletClient } from 'wagmi';
import { useNetwork } from 'wagmi';
import { NETWORKS } from '@/config/networks';
import { useTheme } from "@/providers/ThemeProvider";
import { getBestRpcUrl } from '@/config/rpc';
import { NetworkType } from '@/config/types/rpc';
import { UserTokenSelector } from '@/components/token/UserTokenSelector';

// Token Management ABI with flexible function detection
const TOKEN_ABI = [
  // Basic token info
  "function owner() view returns (address)",
  "function getOwner() view returns (address)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function totalSupply() view returns (uint256)",
  "function decimals() view returns (uint8)",
  
  // Tax Management
  "function buyTax() view returns (uint256)",
  "function sellTax() view returns (uint256)",
  "function setBuyTax(uint256)",
  "function setSellTax(uint256)",
  "function buyTaxLocked() view returns (bool)",
  "function sellTaxLocked() view returns (bool)",
  
  // Tax Distribution
  "function taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
  "function _taxDistribution() view returns (uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
  "function reflectionLocked() view returns (bool)",
  "function autoLiquidityLocked() view returns (bool)",
  "function burnLocked() view returns (bool)",
  "function marketingLocked() view returns (bool)",
  "function developmentLocked() view returns (bool)",
  "function setTaxDistributionValues(uint256 reflection, uint256 autoLiquidity, uint256 burn, uint256 marketing, uint256 development)",
  
  // Wallet Limits
  "function maxWalletAmount() view returns (uint256)",
  "function maxBuyAmount() view returns (uint256)",
  "function maxSellAmount() view returns (uint256)",
  "function maxWalletPercent() view returns (uint256)",
  "function maxBuyPercent() view returns (uint256)",
  "function maxSellPercent() view returns (uint256)",
  "function setMaxWalletPercent(uint256)",
  "function setMaxBuyPercent(uint256)",
  "function setMaxSellPercent(uint256)",
  "function maxWalletLocked() view returns (bool)",
  "function maxBuyLocked() view returns (bool)",
  "function maxSellLocked() view returns (bool)",
  
  // Trading Delay
  "function tradingDelay() view returns (uint256)",
  "function setTradingDelay(uint256)",
  "function tradingDelayLocked() view returns (bool)",
  
  // Trading Status
  "function tradingEnabled() view returns (bool)",
  "function permanentTradingEnabled() view returns (bool)",
  "function tradingEnabledLocked() view returns (bool)",
  {
    inputs: [{ internalType: "bool", name: "enabled", type: "bool" }],
    name: "setTradingEnabled",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    inputs: [],
    name: "tradingEnabled",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  {
    inputs: [],
    name: "tradingEnabledLocked",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "view",
    type: "function",
  },
  
  // Wallet Management
  "function marketingWallets(uint256) view returns (address)",
  "function developmentWallets(uint256) view returns (address)",
  "function setMarketingWallets(address[])",
  "function setDevelopmentWallets(address[])",
  "function marketingWalletsLocked() view returns (bool)",
  "function developmentWalletsLocked() view returns (bool)",
  "event MarketingWalletsUpdated(address[] oldWallets, address[] newWallets)",
  "event DevelopmentWalletsUpdated(address[] oldWallets, address[] newWallets)",
  "function contractLocked() view returns (bool)",
  "function transferOwnership(address newOwner)",
  
  // Burn function
  {
    inputs: [{ internalType: "uint256", name: "amount", type: "uint256" }],
    name: "burn",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
    functionSelector: "0x42966c68"
  },
  {
    inputs: [
      { internalType: "uint256", name: "newMaxWallet", type: "uint256" },
      { internalType: "uint256", name: "newMaxBuy", type: "uint256" },
      { internalType: "uint256", name: "newMaxSell", type: "uint256" },
    ],
    name: "updateTradingLimits",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function",
  },
];

// Helper function to get tax distribution
const getTaxDistribution = async (contract) => {
  try {
    const distribution = await contract.taxDistribution();
    return {
      reflection: distribution[0].toString(),
      autoLiquidity: distribution[1].toString(),
      burn: distribution[2].toString(),
      marketing: distribution[3].toString(),
      development: distribution[4].toString()
    };
  } catch (e) {
    console.error("Error getting tax distribution:", e);
    return {
      reflection: "0",
      autoLiquidity: "0",
      burn: "0",
      marketing: "0",
      development: "0"
    };
  }
};

// Helper function to find the first available function from multiple possibilities
const findAvailableFunction = async (contract: any, functionNames: string[], args: any[] = []) => {
  for (const funcName of functionNames) {
    try {
      if (typeof contract[funcName] === 'function') {
        // For setter functions, just check if they exist without calling them
        if (funcName.startsWith('set')) {
          return { name: funcName, value: null };
        }
        // For getter functions, try to call them
        const result = args.length > 0 ? await contract[funcName](...args) : await contract[funcName]();
        return { name: funcName, value: result };
      }
    } catch (error) {
      console.error(`Error checking function ${funcName}:`, error);
      continue;
    }
  }
  return null;
};

// Function to check if a feature is available in the contract
const isFeatureAvailable = async (contract: any, functionNames: string[]) => {
  return functionNames.some(name => {
    // Check direct function
    if (typeof contract[name] === 'function') return true;
    
    // Check contract.functions
    if (contract.functions && typeof contract.functions[name] === 'function') return true;
    
    // Check if function exists in the functions object with full signature
    if (contract.functions) {
      return Object.keys(contract.functions).some(f => f.startsWith(name));
    }
    
    return false;
  });
};

// Helper function to try multiple function names with better error handling
const tryMultipleFunctions = async (contract, functionNames, defaultValue = null) => {
  for (const name of functionNames) {
    try {
      if (typeof contract[name] === 'function') {
        return await contract[name]();
      }
    } catch (error) {
      console.error(`Error trying function ${name}:`, error);
    }
  }
  return defaultValue;
};

const detectContractLocked = async (contract) => {
  try {
    if (!contract) return false;
    
    // First check if the function exists
    if (typeof contract.contractLocked === 'function') {
      try {
        const isLocked = await contract.contractLocked();
        console.log('Contract master lock status:', isLocked);
        return isLocked;
      } catch (error) {
        console.error('Error calling contractLocked:', error);
        return false;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error detecting contract lock:', error);
    return false;
  }
};

// Helper function to get current wallets
const getCurrentWallets = async (contract: any, type: 'marketing' | 'development') => {
  try {
    const wallets: string[] = [];
    let index = 0;
    
    while (true) {
      try {
        const wallet = await contract[`${type}Wallets`](index);
        if (!wallet || wallet === '0x0000000000000000000000000000000000000000') {
          break;
        }
        wallets.push(wallet);
        index++;
      } catch (error) {
        break;
      }
    }
    
    console.log(`${type} wallets:`, wallets);
    return wallets;
  } catch (error) {
    console.error(`Error getting ${type} wallets:`, error);
    return [];
  }
};

function TokenTools() {
  const { address: userAddress, isConnected } = useAccount();
  const { toast } = useToast();
  const { settings: fees } = useFirebaseFees();
  const { data: walletClient } = useWalletClient();
  const { chain } = useNetwork();
  const { theme } = useTheme();

  // Helper function to get optimized read-only provider
  const getOptimizedProvider = () => {
    if (!chain?.id) return null;
    
    // Map chain ID to NetworkType
    const networkTypeMap: Record<number, NetworkType> = {
      56: 'bsc',
      97: 'bscTestnet',
      1: 'ethereum',
      11155111: 'sepolia',
      43114: 'avalanche',
      43113: 'fuji',
      2741: 'abstract',
      11124: 'abstractTestnet',
      8453: 'base',
      84532: 'baseSepolia',
      10: 'optimism',
      11155420: 'optimismSepolia',
      42161: 'arbitrum',
      421614: 'arbitrumSepolia',
      137: 'polygon',
      80002: 'polygonAmoy',
      1329: 'sei',
      1328: 'seiTestnet'
    };

    const networkType = networkTypeMap[chain.id];
    if (!networkType) {
      console.warn(`No optimized RPC for chain ID ${chain.id}, falling back to wallet provider`);
      return null;
    }

    const rpcUrl = getBestRpcUrl(networkType);
    return new ethers.providers.JsonRpcProvider(rpcUrl);
  };
  const [contractAddress, setContractAddress] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [ownerAddress, setOwnerAddress] = useState("");
  const [tokenInfo, setTokenInfo] = useState({ name: "", symbol: "" });
  const [contract, setContract] = useState<any>(null);
  const [featureStatus, setFeatureStatus] = useState<Record<string, any>>({});
  const [newValue, setNewValue] = useState("");
  const [newOwnerAddress, setNewOwnerAddress] = useState("");
  const [distributions, setDistributions] = useState<Record<string, any>>({
    marketingWallets: [],
    developmentWallets: []
  });
  const [currentWallets, setCurrentWallets] = useState<{
    marketing: string[];
    development: string[];
  }>({
    marketing: [],
    development: []
  });
  const [masterLocked, setMasterLocked] = useState(false);
  const [tradingEnabled, setTradingEnabled] = useState(false);
  const [tradingEnabledLocked, setTradingEnabledLocked] = useState(false);
  const [permanentTradingEnabled, setPermanentTradingEnabled] = useState(false);
  const { settings } = useBurnSettings();

  const [pendingFeature, setPendingFeature] = useState<{ feature: string; value: string } | null>(null);

  // Helper function to get network-specific fee
  const getNetworkFee = (feeType: string) => {
    if (!fees?.networkFees || !chain?.id) return null;

    // Map testnet chain IDs to their mainnet counterparts for fee lookup
    const mainnetChainId = {
      // Ethereum
      1: 1,      // Mainnet
      5: 1,      // Goerli
      11155111: 1, // Sepolia
      11124: 1,  // Abstract Testnet (uses ETH fees)
      2741: 1,   // Abstract Mainnet (uses ETH fees)
      8453: 1,   // Base Mainnet (uses ETH fees)
      84532: 1,  // Base Sepolia (uses ETH fees)
      10: 1,     // OP Mainnet (uses ETH fees)
      11155420: 1, // OP Sepolia (uses ETH fees)
      42161: 1,  // Arbitrum One (uses ETH fees)
      421614: 1, // Arbitrum Sepolia (uses ETH fees)
      
      // BSC
      56: 56,    // Mainnet
      97: 56,    // Testnet
      
      // opBNB
      204: 204,  // Mainnet
      5611: 5611, // Testnet
      
      // Avalanche
      43114: 43114, // Mainnet
      43113: 43114  // Fuji Testnet
    }[chain.id] || chain.id;

    const networkFees = fees.networkFees[mainnetChainId];
    if (!networkFees) return null;

    // Map fee types to their network-specific values
    switch (feeType) {
      case 'toolsFee':
        return networkFees.toolsFee;
      case 'ttTaxDist':
        // Try both possible property names
        return networkFees.ttTaxDist || networkFees.ttTaxDistribution;
      case 'ttWallets':
        return networkFees.ttWallets;
      default:
        return null;
    }
  };

  // Update the fee display in UI
  const getFeeDisplay = (feeType: string) => {
    if (!fees?.networkFees || !chain?.id) return "Connect wallet to view fee";

    const fee = getNetworkFee(feeType);
    if (!fee) return "Fee not configured for this network";

    const networkSymbols = {
      // Ethereum
      1: "ETH",      // Mainnet
      5: "ETH",      // Goerli
      11155111: "ETH", // Sepolia
      11124: "ETH",  // Abstract Testnet
      2741: "ETH",   // Abstract Mainnet
      8453: "ETH",   // Base Mainnet
      84532: "ETH",  // Base Sepolia
      
      // BSC
      56: "BNB",    // Mainnet
      97: "BNB",    // Testnet
      
      // opBNB
      204: "BNB",   // Mainnet
      5611: "BNB",  // Testnet
      
      // Avalanche
      43114: "AVAX", // Mainnet
      43113: "AVAX", // Fuji Testnet
      
      // Sei
      1328: "SEI",   // Atlantic-2 Testnet
      1329: "SEI"    // Pacific-1 Mainnet
    };

    const symbol = networkSymbols[chain.id] || "";
    return `${fee} ${symbol}`;
  };

  // Helper to check if current network is Abstract (ZKsync-based)
  const isAbstractNetwork = () => {
    return chain?.id === 11124 || chain?.id === 2741; // Abstract Testnet or Mainnet
  };

  // Helper to get transaction options with appropriate gas settings
  const getTxOptions = (baseGasLimit: number) => {
    if (isAbstractNetwork()) {
      // For Abstract/ZKsync, don't set gasLimit - let it auto-estimate
      // Setting manual limits on ZKsync can cause estimation issues
      return {};
    }
    return {
      gasLimit: baseGasLimit
    };
  };

  // Function to send platform fee
  const sendPlatformFee = async (feeType: string) => {
    if (!fees?.networkFees || !chain?.id) {
      throw new Error("Fee settings not loaded or chain not detected");
    }

    const fee = getNetworkFee(feeType);
    if (!fee) {
      throw new Error("Fee not configured for this network");
    }

    const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
    const signer = provider.getSigner();
    const feeCollector = fees.collectors[0].address;
    const platformFeeAmount = ethers.utils.parseEther(fee);

    console.log('Sending platform fee transaction...');
    console.log('Fee collector:', feeCollector);
    console.log('Fee amount:', platformFeeAmount.toString());

    const tx = await signer.sendTransaction({
      to: feeCollector,
      value: platformFeeAmount,
      ...getTxOptions(50000)
    });

    await tx.wait();
    console.log('Platform fee transaction confirmed');
  };

  // Constants for fee settings
  const [MODIFICATION_FEE, setMODIFICATION_FEE] = useState("");
  const [FEE_COLLECTOR_ADDRESS, setFEE_COLLECTOR_ADDRESS] = useState("");

  // Wait for fees to be loaded
  useEffect(() => {
    if (!fees) {
      console.log('Waiting for fees to load...');
      return;
    }
    console.log('Fees loaded:', fees);
    setMODIFICATION_FEE(getNetworkFee('toolsFee'));
    setFEE_COLLECTOR_ADDRESS(fees.collectors[0].address);
  }, [fees, chain]);

  // Define feature groups with detection and modification logic
  const featureGroups = {
    buyTax: {
      getters: ['buyTax'],
      setters: ['setBuyTax'],
      lockCheckers: ['buyTaxLocked'],
      description: "Buy Tax Rate",
      icon: DollarSign,
      color: "green",
      valueType: "percentage"
    },
    sellTax: {
      getters: ['sellTax'],
      setters: ['setSellTax'],
      lockCheckers: ['sellTaxLocked'],
      description: "Sell Tax Rate",
      icon: DollarSign,
      color: "red",
      valueType: "percentage"
    },
    reflectionDistribution: {
      getters: ['taxDistribution'],
      setters: ['taxDistribution'],
      lockCheckers: ['reflectionLocked'],
      description: "Reflection Distribution",
      icon: RefreshCw,
      color: "blue",
      valueType: "percentage",
      getValue: async (contract) => {
        const dist = await getTaxDistribution(contract);
        return dist?.reflection?.replace('%', '') || "0";
      }
    },
    autoLiquidityDistribution: {
      getters: ['taxDistribution'],
      setters: ['taxDistribution'],
      lockCheckers: ['autoLiquidityLocked'],
      description: "Auto-Liquidity Distribution",
      icon: RefreshCw,
      color: "purple",
      valueType: "percentage",
      getValue: async (contract) => {
        const dist = await getTaxDistribution(contract);
        return dist?.autoLiquidity?.replace('%', '') || "0";
      }
    },
    burnDistribution: {
      getters: ['taxDistribution'],
      setters: ['taxDistribution'],
      lockCheckers: ['burnLocked'],
      description: "Burn Distribution",
      icon: Flame,
      color: "orange",
      valueType: "percentage",
      getValue: async (contract) => {
        const dist = await getTaxDistribution(contract);
        return dist?.burn?.replace('%', '') || "0";
      }
    },
    marketingDistribution: {
      getters: ['taxDistribution'],
      setters: ['taxDistribution'],
      lockCheckers: ['marketingLocked'],
      description: "Marketing Distribution",
      icon: Wallet,
      color: "yellow",
      valueType: "percentage",
      getValue: async (contract) => {
        const dist = await getTaxDistribution(contract);
        return dist?.marketing?.replace('%', '') || "0";
      }
    },
    developmentDistribution: {
      getters: ['taxDistribution'],
      setters: ['taxDistribution'],
      lockCheckers: ['developmentLocked'],
      description: "Development Distribution",
      icon: Wallet,
      color: "blue",
      valueType: "percentage",
      getValue: async (contract) => {
        const dist = await getTaxDistribution(contract);
        return dist?.development?.replace('%', '') || "0";
      }
    },
    maxWalletAmount: {
      getters: ['maxWalletPercent', 'maxWalletAmount'],
      setters: ['setMaxWalletPercent'],
      lockCheckers: ['maxWalletLocked'],
      description: "Max Wallet Amount",
      icon: Wallet,
      color: "blue",
      valueType: "percentage",
      getValue: async (contract) => {
        try {
          // Try percentage function first
          const maxWalletPercent = await findAvailableFunction(contract, ['maxWalletPercent']);
          if (maxWalletPercent?.value) {
            const percent = maxWalletPercent.value.toNumber() / 100; // Convert basis points to percentage
            return `${percent}%`;
          }
          
          // Fall back to amount-based function
          const maxWallet = await findAvailableFunction(contract, ['maxWalletAmount']);
          if (!maxWallet?.value) return "0";
          
          const value = maxWallet.value.toString();
          
          // Check if it's the max possible uint256 value (unlimited)
          const maxUint256 = "115792089237316195423570985008687907853269984665640564039457584007913129639935";
          if (value === maxUint256) {
            return "Unlimited";
          }
          
          const totalSupply = await contract.totalSupply();
          if (!totalSupply) return "N/A";
          
          // Calculate percentage
          const amount = ethers.BigNumber.from(value);
          const percentage = amount.mul(ethers.BigNumber.from(10000)).div(totalSupply).toNumber() / 100;
          return `${percentage.toFixed(2)}%`;
        } catch (error) {
          console.error("Error getting max wallet:", error);
          return "Error";
        }
      }
    },
    maxBuyAmount: {
      getters: ['maxBuyPercent', 'maxBuyAmount'],
      setters: ['setMaxBuyPercent'],
      lockCheckers: ['maxBuyLocked'],
      description: "Max Buy Amount",
      icon: ArrowLeftRight,
      color: "green",
      valueType: "percentage",
      getValue: async (contract) => {
        try {
          // Try percentage function first
          const maxBuyPercent = await findAvailableFunction(contract, ['maxBuyPercent']);
          if (maxBuyPercent?.value) {
            const percent = maxBuyPercent.value.toNumber() / 100; // Convert basis points to percentage
            return `${percent}%`;
          }
          
          // Fall back to amount-based function
          const maxBuy = await findAvailableFunction(contract, ['maxBuyAmount']);
          if (!maxBuy?.value) return "0";
          
          const value = maxBuy.value.toString();
          const maxUint256 = "115792089237316195423570985008687907853269984665640564039457584007913129639935";
          if (value === maxUint256) return "Unlimited";
          
          const totalSupply = await contract.totalSupply();
          if (!totalSupply) return "N/A";
          
          const amount = ethers.BigNumber.from(value);
          const percentage = amount.mul(ethers.BigNumber.from(10000)).div(totalSupply).toNumber() / 100;
          return `${percentage}%`;
        } catch (error) {
          console.error("Error getting max buy:", error);
          return "Error";
        }
      }
    },
    maxSellAmount: {
      getters: ['maxSellPercent', 'maxSellAmount'],
      setters: ['setMaxSellPercent'],
      lockCheckers: ['maxSellLocked'],
      description: "Max Sell Amount",
      icon: ArrowLeftRight,
      color: "red",
      valueType: "percentage",
      getValue: async (contract) => {
        try {
          // Try percentage function first
          const maxSellPercent = await findAvailableFunction(contract, ['maxSellPercent']);
          if (maxSellPercent?.value) {
            const percent = maxSellPercent.value.toNumber() / 100; // Convert basis points to percentage
            return `${percent}%`;
          }
          
          // Fall back to amount-based function
          const maxSell = await findAvailableFunction(contract, ['maxSellAmount']);
          if (!maxSell?.value) return "0";
          
          const value = maxSell.value.toString();
          const maxUint256 = "115792089237316195423570985008687907853269984665640564039457584007913129639935";
          if (value === maxUint256) return "Unlimited";
          
          const totalSupply = await contract.totalSupply();
          if (!totalSupply) return "N/A";
          
          const amount = ethers.BigNumber.from(value);
          const percentage = amount.mul(ethers.BigNumber.from(10000)).div(totalSupply).toNumber() / 100;
          return `${percentage}%`;
        } catch (error) {
          console.error("Error getting max sell:", error);
          return "Error";
        }
      }
    },
    tradingDelay: {
      getters: ['tradingDelay'],
      setters: ['setTradingDelay'],
      lockCheckers: ['tradingDelayLocked'],
      description: "Trading Delay",
      icon: Lock,
      color: "purple",
      valueType: "seconds"
    },
    tradingEnabled: {
      getters: ['tradingEnabled', 'permanentTradingEnabled'],
      setters: ['setTradingEnabled'],
      lockCheckers: ['tradingEnabledLocked'],
      description: "Trading Status",
      icon: PlayCircle,
      color: "green",
      valueType: "boolean"
    },
    marketingWallets: {
      getters: ['marketingWallets'],
      setters: ['setMarketingWallets'],
      lockCheckers: ['marketingWalletsLocked'],
      description: "Marketing Wallets",
      icon: Wallet,
      color: "yellow",
      valueType: "address"
    },
    developmentWallets: {
      getters: ['developmentWallets'],
      setters: ['setDevelopmentWallets'],
      lockCheckers: ['developmentWalletsLocked'],
      description: "Development Wallets",
      icon: Wallet,
      color: "blue",
      valueType: "address"
    }
  };

  // Feature categories
  const featureCategories = {
    taxes: {
      title: "Tax Settings",
      description: "Configure buy and sell taxes",
      features: ['buyTax', 'sellTax']
    },
    distribution: {
      title: "Tax Distribution",
      description: "Configure how taxes are distributed",
      features: ['reflectionDistribution', 'autoLiquidityDistribution', 'burnDistribution', 'marketingDistribution', 'developmentDistribution']
    },
    limits: {
      title: "Trading Limits",
      description: "Configure trading limits and delays",
      features: ['maxWalletAmount', 'maxBuyAmount', 'maxSellAmount', 'tradingDelay']
    },
    wallets: {
      title: "Wallet Management",
      description: "Configure marketing and development wallets",
      features: ['marketingWallets', 'developmentWallets']
    }
  };

  // Helper function to format large numbers with commas
  const formatNumber = (num: ethers.BigNumber | number): string => {
    const value = typeof num === 'number' ? num : parseFloat(num.toString());
    return new Intl.NumberFormat().format(value);
  };

  // Helper function to format token amounts
  const formatTokenAmount = (amount: ethers.BigNumber): string => {
    try {
      // Remove any decimals and convert to a whole number
      const wholeNumber = amount.div(ethers.BigNumber.from(10).pow(18));
      return new Intl.NumberFormat().format(wholeNumber.toNumber());
    } catch (error) {
      console.error('Error formatting token amount:', error);
      return amount.toString();
    }
  };

  // Format the max amount values without the word "tokens"
  const formatMaxAmount = (value: string) => {
    if (!value) return '';
    // If it contains both percentage and token amount
    if (value.includes('(')) {
      const [percent, amount] = value.split('(');
      return `${percent.trim()} ${amount.replace(' tokens)', ')')})`; 
    }
    return value;
  };

  // Function to scan contract features
  const scanContractFeatures = async (contract: any) => {
    const status: Record<string, any> = {};
    
    // First check trading status and lock status
    try {
      // Get PancakeSwap pair and router addresses directly from public variables
      const [tradingEnabled, permanentEnabled, tradingEnabledLocked] = await Promise.all([
        contract.tradingEnabled(),
        contract.permanentTradingEnabled(),
        contract.tradingEnabledLocked()
      ]);

      setTradingEnabled(tradingEnabled);
      setTradingEnabledLocked(tradingEnabledLocked);
      setPermanentTradingEnabled(permanentEnabled);
      
      console.log('Trading Status:', {
        enabled: tradingEnabled,
        permanent: permanentEnabled,
        locked: tradingEnabledLocked
      });
    } catch (error) {
      console.error('Error checking trading status:', error);
    }
    
    for (const [feature, config] of Object.entries(featureGroups)) {
      try {
        console.log(`Checking feature: ${feature}`);
        console.log(`Detecting ${feature}...`);

        // For tax distribution features, check if the main setTaxDistribution function exists
        const hasGetter = await isFeatureAvailable(contract, config.getters);
        let hasSetter = false;
        
        if (feature.includes('Distribution')) {
          // For distribution features, check if taxDistribution function exists
          console.log('Checking tax distribution setter...');
          const contractFunctions = Object.keys(contract.functions || {});
          console.log('Available contract functions:', contractFunctions.join(', '));
          
          // Check for the taxDistribution function
          hasSetter = typeof contract.taxDistribution === 'function' || 
                     typeof contract.functions?.taxDistribution === 'function' ||
                     contractFunctions.some(f => f === 'taxDistribution' || f.startsWith('taxDistribution('));
          
          console.log('Tax distribution setter exists:', hasSetter);
        } else {
          hasSetter = await isFeatureAvailable(contract, config.setters);
        }
        
        console.log(`${feature} - hasGetter: ${hasGetter}, hasSetter: ${hasSetter}`);
        
        if (!hasGetter && !hasSetter) {
          console.log(`${feature} - No getter or setter found`);
          status[feature] = { exists: false };
          continue;
        }

        // Get current value
        let currentValue;
        if (config.getValue) {
          currentValue = await config.getValue(contract);
        } else {
          const result = await findAvailableFunction(contract, config.getters);
          currentValue = result?.value?.toString() || "0";
        }
        
        console.log(`${feature} - Current value:`, currentValue);

        // Check if locked
        let isLocked = false;
        if (config.lockCheckers) {
          const lockResult = await findAvailableFunction(contract, config.lockCheckers);
          isLocked = lockResult?.value || false;
          console.log(`${feature} - Lock status:`, isLocked);
        }

        // Format value based on type
        let displayValue = currentValue;
        if (config.valueType === "percentage") {
          if (feature === 'maxBuyAmount' || feature === 'maxSellAmount' || feature === 'maxWalletAmount') {
            // Don't add % here as it's already added in the getValue function
            displayValue = currentValue;
          } else {
            displayValue = `${currentValue}%`;
          }
        } else if (config.valueType === "tokens") {
          const totalSupply = await contract.totalSupply();
          if (!totalSupply) return "N/A";
          
          const amount = ethers.BigNumber.from(currentValue);
          const percentage = amount.mul(ethers.BigNumber.from(10000)).div(totalSupply).toNumber() / 100;
          displayValue = `${percentage.toString()}%`;
        } else if (config.valueType === "seconds") {
          displayValue = `${currentValue} seconds`;
        }

        const canModify = hasSetter && !isLocked;
        console.log(`${feature} - Can modify:`, canModify);

        status[feature] = {
          exists: true,
          lockable: true,
          isLocked,
          value: displayValue,
          canModify
        };
      } catch (e) {
        console.error(`Error detecting ${feature}:`, e);
        status[feature] = { exists: false };
      }
    }
    
    console.log('Final feature status:', status);
    setFeatureStatus(status);
  };

  // Function to process the actual feature update
  const processFeatureUpdate = async (feature: string, value: string) => {
    try {
      if (!contract) return;
      if (!fees) {
        toast({
          title: "Error",
          description: "Fee settings not loaded yet. Please try again.",
          variant: "destructive"
        });
        return;
      }

      // Get provider and signer
      const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Send platform fee using Firebase fees
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee('toolsFee'));
      const feeCollectorAddress = fees.collectors[0].address;
      
      console.log('Sending platform fee transaction...');
      console.log('Fee collector:', feeCollectorAddress);
      console.log('Fee amount:', platformFeeAmount.toString());

      const tx = await signer.sendTransaction({
        to: feeCollectorAddress,
        value: platformFeeAmount,
        ...getTxOptions(50000)
      });

      await tx.wait();
      console.log('Platform fee transaction confirmed');

      // Get the setter function name
      const setter = featureGroups[feature].setters[0];
      if (!setter || !contractWithSigner[setter]) {
        console.error(`No setter found for ${feature}`);
        return;
      }

      // Special handling for max amounts (buy, sell, wallet)
      if (feature.includes('max')) {
        // Convert percentage to basis points
        const basisPoints = Math.floor(parseFloat(value) * 100);
        
        // Determine which setter to use based on the feature
        let setterFunction;
        if (feature === 'maxWalletAmount') {
          setterFunction = 'setMaxWalletPercent';
        } else if (feature === 'maxBuyAmount') {
          setterFunction = 'setMaxBuyPercent';
        } else if (feature === 'maxSellAmount') {
          setterFunction = 'setMaxSellPercent';
        }
        
        console.log('Setting max amount:', {
          feature,
          basisPoints,
          setterFunction
        });
        
        // Call the contract with the basis points
        const tx = await contractWithSigner[setterFunction](basisPoints, {
          ...getTxOptions(300000)
        });
        
        toast({
          title: "Transaction Sent",
          description: `Updating ${feature} to ${value}%`,
        });
        
        await tx.wait();
        
        toast({
          title: "Success",
          description: `Successfully updated ${feature}`,
        });
      } else {
        const tx = await contractWithSigner[setter](value, {
          ...getTxOptions(300000)
        });
        await tx.wait();
        
        toast({
          title: "Success",
          description: `Successfully updated ${feature}`,
        });
      }

      // Refresh the UI
      setFeatureStatus(prev => ({
        ...prev,
        [feature]: {
          ...prev[feature],
          value: value
        }
      }));
    } catch (error) {
      console.error('Error updating feature:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update feature",
        variant: "destructive",
      });
    }
  };

  // Function to update a feature
  const updateFeature = async (feature: string, value: string) => {
    try {
      if (!contract) return;

      if (masterLocked) {
        toast({
          title: "Modification Disabled",
          description: "Contract is permanently locked. No modifications are possible.",
          variant: "destructive"
        });
        return;
      }

      await processFeatureUpdate(feature, value);
    } catch (error: any) {
      console.error('Update error:', error);
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update the feature. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Function to update all tax distributions
  const updateAllDistributions = async (newDistributions) => {
    if (!contract || !isOwner) return;
    if (!fees?.networkFees) {
      toast({
        title: "Error",
        description: "Fee settings not loaded yet. Please try again.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Log the raw input for debugging
      console.log('Raw distribution input:', newDistributions);

      // Convert values to integers (floor to ensure we don't round up and exceed 100)
      const reflection = Math.floor(parseFloat(newDistributions.reflection || "0"));
      // Try both property names for auto-liquidity
      const autoLiquidity = Math.floor(parseFloat(
        newDistributions.autoLiquidity || 
        newDistributions.autoLp || 
        "0"
      ));
      const burn = Math.floor(parseFloat(newDistributions.burn || "0"));
      const marketing = Math.floor(parseFloat(newDistributions.marketing || "0"));
      const development = Math.floor(parseFloat(newDistributions.development || "0"));

      // Log parsed values
      console.log('Parsed distribution values:', {
        reflection,
        autoLiquidity,
        burn,
        marketing,
        development
      });

      // Validate total equals 100
      const total = reflection + autoLiquidity + burn + marketing + development;
      console.log('Tax distribution total calculation:', {
        reflection,
        autoLiquidity,
        burn,
        marketing,
        development,
        total,
        calculation: `${reflection}% + ${autoLiquidity}% + ${burn}% + ${marketing}% + ${development}% = ${total}%`
      });
      
      if (total !== 100) {
        throw new Error(`Total distribution must equal 100%. Current total: ${total}% (${reflection}% + ${autoLiquidity}% + ${burn}% + ${marketing}% + ${development}%)`);
      }

      // Get provider and signer
      const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Get the tax distribution fee
      const taxDistFee = getNetworkFee('ttTaxDist');
      if (!taxDistFee) {
        console.error('Tax distribution fee not found:', fees);
        console.log('Available fees:', fees); // Log all available fees
        toast({
          title: "Error",
          description: "Tax distribution fee not configured. Please try again later.",
          variant: "destructive"
        });
        return;
      }

      // Send platform fee
      await sendPlatformFee('ttTaxDist');
      
      toast({
        title: "Platform Fee Paid",
        description: "Successfully paid the platform fee. Please confirm the next transaction to update the tax distribution.",
      });

      // Now proceed with the contract update
      console.log('Sending contract update transaction...');
      const tx = await contractWithSigner.setTaxDistributionValues(
        reflection.toString(),
        autoLiquidity.toString(),
        burn.toString(),
        marketing.toString(),
        development.toString(),
        {
          ...getTxOptions(300000)
        }
      );

      console.log('Transaction sent:', tx.hash);
      
      toast({
        title: "Transaction Sent",
        description: "Updating tax distribution values...",
      });

      await tx.wait();

      // Refresh the values
      await scanContractFeatures(contract);

      toast({
        title: "Success",
        description: "Tax distribution updated successfully",
      });
    } catch (error) {
      console.error('Error updating all distributions:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update tax distributions",
        variant: "destructive",
      });
    }
  };

  // Separate update functions for marketing and development wallets
  const updateMarketingWallet = async (address: string) => {
    if (!contract) return;
    if (!fees) {
      toast({
        title: "Error",
        description: "Fee settings not loaded yet. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      if (masterLocked) {
        toast({
          title: "Modification Disabled",
          description: "Contract is permanently locked. No modifications are possible.",
          variant: "destructive"
        });
        return;
      }

      // Get provider and signer
      const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Send platform fee using Firebase fees
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee('toolsFee'));
      const feeCollectorAddress = fees.collectors[0].address;
      
      console.log('Sending platform fee transaction...');
      console.log('Fee collector:', feeCollectorAddress);
      console.log('Fee amount:', platformFeeAmount.toString());
      
      const platformFeeTx = await signer.sendTransaction({
        to: feeCollectorAddress,
        value: platformFeeAmount,
        ...getTxOptions(50000)
      });
      
      console.log('Platform fee transaction sent:', platformFeeTx.hash);
      
      toast({
        title: "Processing Fee Payment",
        description: "Please wait while the fee payment is being processed...",
      });

      await platformFeeTx.wait();
      
      toast({
        title: "Platform Fee Paid",
        description: "Successfully paid the platform fee. Please confirm the next transaction to update the marketing wallet.",
      });

      // Update marketing wallets with just this address
      const tx = await contractWithSigner.setMarketingWallets([address], {
        ...getTxOptions(300000)
      });

      toast({
        title: "Transaction Sent",
        description: "Updating marketing wallet...",
      });

      await tx.wait();

      // Immediately update the UI with the new wallet address
      setFeatureStatus(prev => ({
        ...prev,
        marketingWallets: {
          ...prev.marketingWallets,
          value: address
        }
      }));
      
      setCurrentWallets(prev => ({
        ...prev,
        marketing: [address]
      }));

      toast({
        title: "Success",
        description: "Marketing wallet updated successfully",
      });
    } catch (error) {
      console.error('Error updating marketing wallet:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update marketing wallet",
        variant: "destructive",
      });
    }
  };

  const updateDevelopmentWallet = async (address: string) => {
    if (!contract) return;
    if (!fees) {
      toast({
        title: "Error",
        description: "Fee settings not loaded yet. Please try again.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      if (masterLocked) {
        toast({
          title: "Modification Disabled",
          description: "Contract is permanently locked. No modifications are possible.",
          variant: "destructive"
        });
        return;
      }

      // Get provider and signer
      const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Send platform fee using Firebase fees
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee('toolsFee'));
      const feeCollectorAddress = fees.collectors[0].address;
      
      console.log('Sending platform fee transaction...');
      console.log('Fee collector:', feeCollectorAddress);
      console.log('Fee amount:', platformFeeAmount.toString());
      
      const platformFeeTx = await signer.sendTransaction({
        to: feeCollectorAddress,
        value: platformFeeAmount,
        ...getTxOptions(50000)
      });
      
      console.log('Platform fee transaction sent:', platformFeeTx.hash);
      
      toast({
        title: "Processing Fee Payment",
        description: "Please wait while the fee payment is being processed...",
      });

      await platformFeeTx.wait();
      
      toast({
        title: "Platform Fee Paid",
        description: "Successfully paid the platform fee. Please confirm the next transaction to update the development wallet.",
      });

      // Update development wallets with just this address
      const tx = await contractWithSigner.setDevelopmentWallets([address], {
        ...getTxOptions(300000)
      });

      toast({
        title: "Transaction Sent",
        description: "Updating development wallet...",
      });

      await tx.wait();

      // Immediately update the UI with the new wallet address
      setFeatureStatus(prev => ({
        ...prev,
        developmentWallets: {
          ...prev.developmentWallets,
          value: address
        }
      }));
      
      setCurrentWallets(prev => ({
        ...prev,
        development: [address]
      }));

      toast({
        title: "Success",
        description: "Development wallet updated successfully",
      });
    } catch (error) {
      console.error('Error updating development wallet:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update development wallet",
        variant: "destructive",
      });
    }
  };

  const updateAllWallets = async (wallets: any) => {
    try {
      if (masterLocked) {
        toast({
          title: "Modification Disabled",
          description: "Contract is permanently locked. No modifications are possible.",
          variant: "destructive"
        });
        return;
      }

      const { marketingWallets, developmentWallets } = wallets;
      
      // Validate addresses
      const validMarketingWallets = marketingWallets.filter((addr: string) => ethers.utils.isAddress(addr));
      const validDevelopmentWallets = developmentWallets.filter((addr: string) => ethers.utils.isAddress(addr));
      
      if (validMarketingWallets.length === 0 && validDevelopmentWallets.length === 0) {
        throw new Error("No valid wallet addresses provided");
      }

      // Send platform fee first
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee('toolsFee'));
      const feeCollector = fees.collectors[0].address;
      
      console.log('Sending platform fee transaction...');
      
      const platformFeeTx = await contract.signer.sendTransaction({
        to: feeCollector,
        value: platformFeeAmount,
        ...getTxOptions(21000) // Standard gas limit for BNB transfer
      });

      await platformFeeTx.wait();
      console.log('Platform fee transaction confirmed');

      // Update marketing wallets
      console.log('Updating marketing wallets:', validMarketingWallets);
      const marketingTx = await contract.setMarketingWallets(validMarketingWallets);
      const marketingReceipt = await marketingTx.wait();
      
      if (marketingReceipt.status === 1) {
        console.log('Marketing wallets updated successfully');
        
        // Update development wallets
        if (validDevelopmentWallets.length > 0) {
          console.log('Updating development wallets:', validDevelopmentWallets);
          const developmentTx = await contract.setDevelopmentWallets(validDevelopmentWallets);
          const developmentReceipt = await developmentTx.wait();
          
          if (developmentReceipt.status === 1) {
            console.log('Development wallets updated successfully');
          }
        }
        
        // Refresh wallet lists
        const [marketing, development] = await Promise.all([
          getCurrentWallets(contract, 'marketing'),
          getCurrentWallets(contract, 'development')
        ]);
        setCurrentWallets({ marketing, development });

        toast({
          title: "Success",
          description: "The wallet addresses have been successfully updated",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error updating wallets:', error);
      // Check if the error is from the contract or transaction
      const errorMessage = error.reason || error.message || "Failed to update wallet addresses";
      
      // If the transaction was successful but we got a revert, it might still have worked
      if (error.code === 'CALL_EXCEPTION' && error.receipt?.status === 1) {
        toast({
          title: "Success",
          description: "The wallet addresses have been successfully updated",
          variant: "default"
        });
        
        // Refresh wallet lists to confirm
        const [marketing, development] = await Promise.all([
          getCurrentWallets(contract, 'marketing'),
          getCurrentWallets(contract, 'development')
        ]);
        setCurrentWallets({ marketing, development });
      } else {
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const validateInput = (feature: string, value: string): string | null => {
    // Basic validation
    if (!value) return "Value is required";

    // For wallet addresses
    if (feature.toLowerCase().includes('wallet') && !feature.includes('maxWallet')) {
      if (!ethers.utils.isAddress(value)) {
        return `Invalid wallet address: ${value}`;
      }
    }

    // For percentage values (including maxWallet)
    if (feature.includes('max') || feature.includes('tax') || feature.includes('Distribution')) {
      const num = parseFloat(value);
      if (isNaN(num)) return "Must be a number";
      if (num < 0) return "Cannot be negative";
      if (num > 100) return "Cannot exceed 100%";
    }

    return null;
  };

  // Function to validate contract and ownership
  const validateContractAndOwnership = async () => {
    if (!isConnected) {
      toast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet first to verify ownership.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Basic address format validation
      if (!contractAddress || !contractAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
        toast({
          title: "Invalid Address",
          description: "Please enter a valid contract address.",
          variant: "destructive"
        });
        return;
      }

      setIsValidating(true);
      setTokenInfo({ name: "", symbol: "" });
      setOwnerAddress("");
      setFeatureStatus({});
    
      // Use optimized provider for read operations
      const optimizedProvider = getOptimizedProvider();
      const readProvider = optimizedProvider || (walletClient ? new ethers.providers.Web3Provider(walletClient.transport, "any") : null);
      
      if (!readProvider) {
        throw new Error("No provider available. Please make sure your wallet is connected.");
      }
      
      // Create contract instance with read-only provider for faster queries
      const contractInstance = new ethers.Contract(
        contractAddress,
        TOKEN_ABI,
        readProvider
      );
      
      // Store the contract instance
      setContract(contractInstance);

      // Fetch token info
      try {
        const [name, symbol] = await Promise.all([
          contractInstance.name(),
          contractInstance.symbol()
        ]);
        setTokenInfo({ name, symbol });
      } catch (error) {
        console.error('Error fetching token info:', error);
        throw new Error("Could not fetch token information. This might not be a valid token contract.");
      }

      // Check ownership
      let contractOwner;
      try {
        contractOwner = await contractInstance.owner();
      } catch (error) {
        try {
          contractOwner = await contractInstance.getOwner();
        } catch (innerError) {
          throw new Error("Could not verify contract ownership. The contract might not support ownership verification.");
        }
      }

      setOwnerAddress(contractOwner);
      const isUserOwner = userAddress?.toLowerCase() === contractOwner.toLowerCase();
      setIsOwner(isUserOwner);

      // Scan for available features
      await scanContractFeatures(contractInstance);

      if (!isUserOwner) {
        toast({
          title: "Not Contract Owner",
          description: "You must be the contract owner to modify token settings.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Verification Successful",
          description: "Contract ownership verified. You can now modify available token settings.",
          variant: "default"
        });
      }
    } catch (error: any) {
      console.error('Validation error:', error);
      toast({
        title: "Verification Failed",
        description: error.message || "An error occurred while verifying the contract.",
        variant: "destructive"
      });
      setIsOwner(false);
      setContract(null);
    } finally {
      setIsValidating(false);
    }
  };

  const [distributionTotal, setDistributionTotal] = useState(0);

  // Define fetchWallets function outside of useEffect
  const fetchWallets = async () => {
    if (contract) {
      try {
        // Fetch current wallets
        const [marketing, development] = await Promise.all([
          getCurrentWallets(contract, 'marketing'),
          getCurrentWallets(contract, 'development')
        ]);
        
        setCurrentWallets({ marketing, development });
        
        // Update feature status display
        setFeatureStatus(prev => ({
          ...prev,
          marketingWallets: {
            ...prev.marketingWallets,
            value: marketing.length > 0 
              ? marketing.map((w: string) => `${w.substring(0, 6)}...${w.substring(w.length - 4)}`).join(', ')
              : 'No marketing wallets set'
          },
          developmentWallets: {
            ...prev.developmentWallets,
            value: development.length > 0
              ? development.map((w: string) => `${w.substring(0, 6)}...${w.substring(w.length - 4)}`).join(', ')
              : 'No development wallets set'
          }
        }));
      } catch (error) {
        console.error('Error fetching wallets:', error);
      }
    }
  };

  // Use fetchWallets in useEffect
  useEffect(() => {
    fetchWallets();
  }, [contract]);

  // Function to check if wallets are locked
  const checkWalletLockStatus = async (contract: any) => {
    try {
      // Check if the lock checker functions exist
      const marketingLockChecker = featureGroups.marketingWallets.lockCheckers[0];
      const developmentLockChecker = featureGroups.developmentWallets.lockCheckers[0];

      const [marketingLocked, developmentLocked] = await Promise.all([
        contract[marketingLockChecker](),
        contract[developmentLockChecker]()
      ]);

      console.log('Marketing Wallet Locked:', marketingLocked);
      console.log('Development Wallet Locked:', developmentLocked);

      setFeatureStatus(prev => ({
        ...prev,
        marketingWallets: {
          ...prev.marketingWallets,
          exists: true,
          canModify: !marketingLocked && isOwner,
          isLocked: marketingLocked
        },
        developmentWallets: {
          ...prev.developmentWallets,
          exists: true,
          canModify: !developmentLocked && isOwner,
          isLocked: developmentLocked
        }
      }));
    } catch (error) {
      console.error('Error checking wallet lock status:', error);
    }
  };

  // Use checkWalletLockStatus in useEffect
  useEffect(() => {
    if (contract && isOwner) {
      checkWalletLockStatus(contract);
    }
  }, [contract, isOwner]);

  useEffect(() => {
    const checkMasterLock = async () => {
      if (contract) {
        const locked = await detectContractLocked(contract);
        console.log('Setting master lock status to:', locked);
        setMasterLocked(locked);
      }
    };
    checkMasterLock();
  }, [contract]);

  useEffect(() => {
    const checkTradingStatus = async () => {
      if (contract) {
        const enabled = await contract.tradingEnabled();
        console.log('Setting trading status to:', enabled);
        setTradingEnabled(enabled);
      }
    };
    checkTradingStatus();
  }, [contract]);

  useEffect(() => {
    const checkTradingEnabledLock = async () => {
      if (contract && featureGroups.tradingEnabled.lockCheckers?.[0]) {
        try {
          const tradingEnabledLockChecker = featureGroups.tradingEnabled.lockCheckers[0];
          const locked = await contract[tradingEnabledLockChecker]();
          console.log('Setting trading enabled lock status to:', locked);
          setTradingEnabledLocked(locked);
        } catch (error) {
          console.log('Trading enabled lock check not available or failed');
          setTradingEnabledLocked(false);
        }
      }
    };
    checkTradingEnabledLock();
  }, [contract]);

  useEffect(() => {
    const checkPermanentTradingEnabled = async () => {
      if (contract) {
        try {
          const enabled = await contract.permanentTradingEnabled();
          console.log('Setting permanent trading enabled status to:', enabled);
          setPermanentTradingEnabled(enabled);
        } catch (error) {
          console.log('Permanent trading enabled check not available or failed');
          setPermanentTradingEnabled(false);
        }
      }
    };
    checkPermanentTradingEnabled();
  }, [contract]);

  const [currentNetwork, setCurrentNetwork] = useState(NETWORKS.BSC);

  useEffect(() => {
    if (chain) {
      const chainIdHex = `0x${chain.id.toString(16)}`;
      switch (chainIdHex) {
        case NETWORKS.BSC.chainId:
          setCurrentNetwork(NETWORKS.BSC);
          break;
        case NETWORKS.BSC_TESTNET.chainId:
          setCurrentNetwork(NETWORKS.BSC_TESTNET);
          break;
        case NETWORKS.ETHEREUM.chainId:
          setCurrentNetwork(NETWORKS.ETHEREUM);
          break;
        case NETWORKS.SEPOLIA.chainId:
          setCurrentNetwork(NETWORKS.SEPOLIA);
          break;
        case NETWORKS.AVALANCHE.chainId:
          setCurrentNetwork(NETWORKS.AVALANCHE);
          break;
        case NETWORKS.FUJI.chainId:
          setCurrentNetwork(NETWORKS.FUJI);
          break;
        case NETWORKS.POLYGON.chainId:
          setCurrentNetwork(NETWORKS.POLYGON);
          break;
        case NETWORKS.POLYGON_AMOY.chainId:
          setCurrentNetwork(NETWORKS.POLYGON_AMOY);
          break;
        case NETWORKS.ABSTRACT.chainId:
          setCurrentNetwork(NETWORKS.ABSTRACT);
          break;
        case NETWORKS.ABSTRACT_TESTNET.chainId:
          setCurrentNetwork(NETWORKS.ABSTRACT_TESTNET);
          break;
        case NETWORKS.BASE.chainId:
          setCurrentNetwork(NETWORKS.BASE);
          break;
        case NETWORKS.BASE_SEPOLIA.chainId:
          setCurrentNetwork(NETWORKS.BASE_SEPOLIA);
          break;
        case NETWORKS.OPTIMISM.chainId:
          setCurrentNetwork(NETWORKS.OPTIMISM);
          break;
        case NETWORKS.OPTIMISM_SEPOLIA.chainId:
          setCurrentNetwork(NETWORKS.OPTIMISM_SEPOLIA);
          break;
        case NETWORKS.ARBITRUM.chainId:
          setCurrentNetwork(NETWORKS.ARBITRUM);
          break;
        case NETWORKS.ARBITRUM_SEPOLIA.chainId:
          setCurrentNetwork(NETWORKS.ARBITRUM_SEPOLIA);
          break;
        case '0x530': // Sei Atlantic-2 Testnet (1328)
          setCurrentNetwork({ name: 'Sei', chainId: '0x530' });
          break;
        case '0x531': // Sei Pacific-1 Mainnet (1329)
          setCurrentNetwork({ name: 'Sei', chainId: '0x531' });
          break;
        default:
          console.warn('Unsupported network detected:', chain.id);
      }
    }
  }, [chain]);

  const [burnAmount, setBurnAmount] = useState<string>("");
  const [isBurning, setIsBurning] = useState(false);
  const [totalSupply, setTotalSupply] = useState<string>("0");

  // Function to fetch and update total supply
  const updateTotalSupply = async () => {
    if (contract) {
      try {
        const supply = await contract.totalSupply();
        const decimals = await contract.decimals();
        const formattedSupply = ethers.utils.formatUnits(supply, decimals);
        setTotalSupply(formattedSupply);
      } catch (error) {
        console.error("Error fetching total supply:", error);
      }
    }
  };

  // Update total supply when contract changes or after successful burn
  useEffect(() => {
    updateTotalSupply();
  }, [contract]);

  const burnTokens = async () => {
    if (!contract || !burnAmount) {
      toast({
        title: "Error",
        description: "Please enter a valid burn amount",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsBurning(true);

      // Get provider and signer
      const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Send platform fee
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee('toolsFee'));
      const feeCollectorAddress = fees.collectors[0].address;
      
      console.log('Sending platform fee transaction...');
      console.log('Fee collector:', feeCollectorAddress);
      console.log('Fee amount:', platformFeeAmount.toString());

      const tx = await signer.sendTransaction({
        to: feeCollectorAddress,
        value: platformFeeAmount,
        ...getTxOptions(50000)
      });

      await tx.wait();
      console.log('Platform fee transaction confirmed');

      // Get current supply and limits before burn
      const [currentSupply, decimals, currentMaxWallet, currentMaxBuy, currentMaxSell] = await Promise.all([
        contract.totalSupply(),
        contract.decimals(),
        contract.maxWalletAmount(),
        contract.maxBuyAmount(),
        contract.maxSellAmount()
      ]);

      // Calculate current percentages relative to max wallet
      const maxBuyPercent = currentMaxBuy.mul(ethers.BigNumber.from(10000)).div(currentMaxWallet);
      const maxSellPercent = currentMaxSell.mul(ethers.BigNumber.from(10000)).div(currentMaxWallet);

      // Calculate burn amount with decimals
      const burnAmountWithDecimals = ethers.utils.parseUnits(burnAmount, decimals);

      // Execute burn transaction
      const burnTx = await contractWithSigner.burn(burnAmountWithDecimals, {
        ...getTxOptions(500000)
      });

      toast({
        title: "Transaction Sent",
        description: "Burning tokens...",
      });

      // Wait for burn transaction
      await burnTx.wait();

      // Get new total supply
      const newTotalSupply = await contract.totalSupply();
      const formattedTotalSupply = ethers.utils.formatUnits(newTotalSupply, decimals);
      setTotalSupply(formattedTotalSupply);

      try {
        // Update max wallet amount first
        const newMaxWallet = newTotalSupply;
        const setMaxWalletTx = await contractWithSigner.setMaxWalletAmount(newMaxWallet, {
          ...getTxOptions(300000)
        });
        await setMaxWalletTx.wait();

        // Calculate new max buy/sell based on percentages of new max wallet
        const newMaxBuy = newMaxWallet.mul(maxBuyPercent).div(ethers.BigNumber.from(10000));
        const newMaxSell = newMaxWallet.mul(maxSellPercent).div(ethers.BigNumber.from(10000));

        // Update max buy amount
        const setMaxBuyTx = await contractWithSigner.setMaxBuyAmount(newMaxBuy, {
          ...getTxOptions(300000)
        });
        await setMaxBuyTx.wait();

        // Update max sell amount
        const setMaxSellTx = await contractWithSigner.setMaxSellAmount(newMaxSell, {
          ...getTxOptions(300000)
        });
        await setMaxSellTx.wait();

        toast({
          title: "Success",
          description: "Trading limits updated successfully",
        });
      } catch (limitsError) {
        console.error('Error updating trading limits:', limitsError);
        toast({
          title: "Warning",
          description: "Tokens burned successfully but failed to update trading limits. Manual update may be required.",
          variant: "destructive"
        });
      }

      // Reset burn amount input
      setBurnAmount("");

      // Show success message
      toast({
        title: "Success",
        description: "Tokens burned successfully",
        variant: "default"
      });

      // Refresh contract features
      await scanContractFeatures(contract);
      
    } catch (error: any) {
      console.error('Error burning tokens:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to burn tokens",
        variant: "destructive"
      });
    } finally {
      setIsBurning(false);
    }
  };

  const updateTradingLimits = async (type: string, value: string) => {
    if (!contract || !isOwner) return;
    if (!fees) {
      toast({
        title: "Error",
        description: "Fee settings not loaded yet. Please try again.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Convert percentage to basis points (multiply by 100)
      const basisPoints = Math.floor(parseFloat(value) * 100);
      
      if (isNaN(basisPoints) || basisPoints < 0 || basisPoints > 10000) {
        throw new Error('Invalid percentage value. Must be between 0 and 100');
      }

      // Get provider and signer
      const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
      const signer = provider.getSigner();
      const contractWithSigner = contract.connect(signer);

      // Send platform fee
      const platformFeeAmount = ethers.utils.parseEther(getNetworkFee('toolsFee'));
      const feeCollectorAddress = fees.collectors[0].address;
      
      console.log('Sending platform fee transaction...');
      const platformFeeTx = await signer.sendTransaction({
        to: feeCollectorAddress,
        value: platformFeeAmount,
        ...getTxOptions(50000)
      });
      
      toast({
        title: "Processing Fee Payment",
        description: "Please wait while the fee payment is being processed...",
      });

      await platformFeeTx.wait();
      
      toast({
        title: "Platform Fee Paid",
        description: "Successfully paid the platform fee. Please confirm the next transaction to update the trading limit.",
      });

      // Try to determine if the contract supports the new percentage-based functions
      const hasPercentFunctions = await isFeatureAvailable(contractWithSigner, ['setMaxWalletPercent']);

      let tx;
      if (hasPercentFunctions) {
        // Use new percentage-based functions
        switch (type) {
          case 'maxWalletAmount':
            tx = await contractWithSigner.setMaxWalletPercent(basisPoints, { ...getTxOptions(300000) });
            break;
          case 'maxBuyAmount':
            tx = await contractWithSigner.setMaxBuyPercent(basisPoints, { ...getTxOptions(300000) });
            break;
          case 'maxSellAmount':
            tx = await contractWithSigner.setMaxSellPercent(basisPoints, { ...getTxOptions(300000) });
            break;
          default:
            throw new Error('Invalid trading limit type');
        }
      } else {
        // Use old updateTradingLimits function
        const totalSupply = await contract.totalSupply();
        console.log('Setting max amount - Total supply:', totalSupply.toString());
        console.log('Input percentage:', value);
        
        // Calculate token amount based on percentage
        const amount = totalSupply.mul(ethers.BigNumber.from(basisPoints));
        console.log('Setting max amount:', { totalSupply: totalSupply.toString(), percentage: value, amount: amount.toString() });
        
        // Get current values for other limits
        const currentMaxWallet = await findAvailableFunction(contract, ['maxWalletAmount']);
        const currentMaxBuy = await findAvailableFunction(contract, ['maxBuyAmount']);
        const currentMaxSell = await findAvailableFunction(contract, ['maxSellAmount']);
        
        // Prepare values for updateTradingLimits
        let maxWallet = currentMaxWallet?.value || totalSupply;
        let maxBuy = currentMaxBuy?.value || totalSupply;
        let maxSell = currentMaxSell?.value || totalSupply;
        
        // Update the appropriate limit
        switch (type) {
          case 'maxWalletAmount':
            maxWallet = amount;
            break;
          case 'maxBuyAmount':
            maxBuy = amount;
            break;
          case 'maxSellAmount':
            maxSell = amount;
            break;
          default:
            throw new Error('Invalid trading limit type');
        }
        
        tx = await contractWithSigner.updateTradingLimits(
          maxWallet,
          maxBuy,
          maxSell,
          { ...getTxOptions(300000) }
        );
      }

      console.log('Transaction sent:', tx.hash);
      
      toast({
        title: "Transaction Sent",
        description: "Updating trading limit...",
      });

      await tx.wait();
      console.log('Transaction confirmed');

      // Refresh the values
      await scanContractFeatures(contract);

      toast({
        title: "Success",
        description: "Trading limit updated successfully",
      });
    } catch (error) {
      console.error('Error updating trading limit:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update trading limit",
        variant: "destructive",
      });
    }
  };

  // Get network-specific names
  const getNetworkNames = () => {
    if (!chain) return { networkName: "BSC", networkDesc: "BSC" };
    
    switch (chain.id) {
      case 1: // Ethereum Mainnet
        return { networkName: "Ethereum", networkDesc: "Ethereum Mainnet" };
      case 11155111: // Sepolia
        return { networkName: "Sepolia", networkDesc: "Sepolia Testnet" };
      case 56: // BSC
        return { networkName: "BSC", networkDesc: "BSC" };
      case 97: // BSC Testnet
        return { networkName: "BSC Testnet", networkDesc: "BSC Testnet" };
      case 43114: // Avalanche
        return { networkName: "Avalanche", networkDesc: "Avalanche" };
      case 43113: // Fuji
        return { networkName: "Fuji", networkDesc: "Avalanche Fuji Testnet" };
      case 2741: // Abstract Mainnet
        return { networkName: "Abstract", networkDesc: "Abstract Mainnet" };
      case 11124: // Abstract Testnet
        return { networkName: "Abstract Sepolia", networkDesc: "Abstract Sepolia Testnet" };
      case 8453: // Base Mainnet
        return { networkName: "Base", networkDesc: "Base" };
      case 84532: // Base Sepolia
        return { networkName: "Base Sepolia", networkDesc: "Base Sepolia Testnet" };
      case 1328: // Sei Atlantic-2 Testnet
        return { networkName: "Sei", networkDesc: "Sei Atlantic-2 Testnet" };
      case 1329: // Sei Pacific-1 Mainnet
        return { networkName: "Sei", networkDesc: "Sei Pacific-1 Mainnet" };
      default:
        return { networkName: "BSC", networkDesc: "BSC" };
    }
  };

  const { networkName, networkDesc } = getNetworkNames();

  const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="border-b last:border-b-0" style={{
        borderColor: `var(--color-header-accent, #0A74FF)30`
      }}>
        <button
          className="w-full py-5 px-4 flex justify-between items-center hover:opacity-80 transition-colors duration-200"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="text-lg font-medium" style={{
            color: `var(--color-header-accent, #0A74FF)`
          }}>{question}</span>
          <span className={`transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{
            color: `var(--color-secondary, #10b981)`
          }}>
            ▼
          </span>
        </button>
        <div
          className={`overflow-hidden transition-all duration-200 ${
            isOpen ? 'max-h-96 py-4 px-4' : 'max-h-0'
          }`}
        >
          <p className="text-gray-300">{answer}</p>
        </div>
      </div>
    );
  };

  const faqData = [
    {
      question: "What are Token Tools?",
      answer: "Token Tools is a comprehensive suite of utilities that allows you to manage and modify your token's settings after deployment. You can adjust tax rates, transfer ownership, manage trading limits, and perform other administrative functions."
    },
    {
      question: "How do I modify tax rates?",
      answer: "To modify tax rates, enter your token's contract address and connect your wallet as the contract owner. You can then adjust buy tax, sell tax, and tax distribution settings. Remember that tax rates are set in basis points (100 = 1%)."
    },
    {
      question: "How do trading limits work?",
      answer: "Trading limits (max wallet, max buy, max sell) are set as percentages of the total supply. For example, setting max wallet to 500 basis points means each wallet can hold up to 5% of the total supply. These limits help prevent large holders from manipulating the market."
    },
    {
      question: "Can I transfer ownership of my token?",
      answer: "Yes, you can transfer ownership to a new address. The new owner will have full administrative rights to the contract. Make sure to double-check the new owner's address as this action can be irreversible if ownership is later renounced."
    },
    {
      question: "What happens if I burn tokens?",
      answer: "When you burn tokens, they are permanently removed from the total supply. This is irreversible and can make your token more scarce. The burn function sends tokens to a dead address (0x000...dead) where they can never be accessed again."
    },
    {
      question: "Do I need to pay fees to use Token Tools?",
      answer: "Some functions may require a small platform fee to cover operational costs. The exact fee amount will be displayed before you confirm any transaction. Additionally, all blockchain transactions require network gas fees."
    }
  ];

  return (
    <>
      <div className="min-h-[calc(100vh-theme(spacing.32))] flex flex-col">
        <div className="max-w-2xl mx-auto w-full flex-1 flex flex-col gap-4">
        {/* Title Section */}
        <div className="text-center mb-4 mt-8">
          <div className="flex items-center justify-center gap-2 sm:gap-4">
            <Logo className="w-8 h-8 sm:w-10 sm:h-10" showTools={true} />
            <div className="flex items-center">
              <h1 className="text-2xl sm:text-4xl font-bold bg-clip-text text-transparent pb-1" style={{
                backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
              }}>{networkName} Token Tools</h1>
            </div>
            <Logo className="w-8 h-8 sm:w-10 sm:h-10" showTools={true} />
          </div>
          <p className="text-muted-foreground mt-2 sm:mt-4 text-sm sm:text-base px-4">
            Manage and configure your token's features
            <br />
            Scan/Analyze/Modify your {networkDesc} token contracts features. Tool is designed for Contracts deployed by the MdbApp.io factory.
          </p>
        </div>

        {/* Contract Input Section */}
        <div className="relative rounded-lg">
          <div className="absolute inset-0 rounded-lg bg-[length:25%_100%] bg-gradient-to-r from-[var(--color-header-accent,#0A74FF)] via-[var(--color-header-accent,#0A74FF)] to-[var(--color-header-accent,#0A74FF)] bg-repeat-x" />
          <div className="absolute inset-[1px] rounded-lg dark:bg-black bg-white" />
          <Card className="relative bg-background/30 backdrop-blur-sm rounded-lg border-0">
            <CardHeader className="text-center">
              <div className="flex justify-center items-center gap-2">
                <CardTitle>Connect Contract</CardTitle>
              </div>
              <CardDescription className="text-center">
                This tool is exclusively available to contract owners. To use these features:
                <ul className="mt-2 space-y-2 text-left list-none">
                  <li className="flex items-center gap-2 justify-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Connect the wallet that deployed the contract
                  </li>
                  <li className="flex items-center gap-2 justify-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Enter your contract address below
                  </li>
                  <li className="flex items-center gap-2 justify-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    Verify ownership to access token settings
                  </li>
                </ul>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 items-center">
                <UserTokenSelector
                  onSelectToken={(address) => setContractAddress(address)}
                  selectedToken={contractAddress}
                  label="Quick Select Your Token"
                />
                <Input
                  placeholder="Enter contract address or select from your tokens above"
                  value={contractAddress}
                  onChange={(e) => setContractAddress(e.target.value)}
                  className="w-full bg-black/50 border-blue-500/50 text-white placeholder:text-blue-200/50"
                />
                <div className="relative rounded-md w-full">
                  <div 
                    className="absolute inset-0 rounded-md bg-[length:25%_100%] bg-repeat-x" 
                    style={{ 
                      backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-text, #ffffff), var(--color-header-accent, #0A74FF))` 
                    }}
                  />
                  <div className="absolute inset-[1px] rounded-[calc(0.375rem-1px)] dark:bg-black bg-white" />
                  <Button
                    onClick={validateContractAndOwnership}
                    disabled={isValidating || !isConnected}
                    className="relative w-full dark:bg-black/30 bg-white/30 backdrop-blur-sm border-0 hover:bg-accent/10 transition-colors text-foreground font-semibold"
                  >
                    {isValidating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Verifying
                      </>
                    ) : (
                      "Verify Owner"
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Token Info Card */}
        {tokenInfo.name && (
          <Card className="mb-4 border-[var(--color-header-accent)]/50 hover:border-[var(--color-header-accent)]/70 transition-colors">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2">
                <BadgeCheck className="h-6 w-6 text-[var(--color-header-accent)]" />
                <CardTitle>Connected Token</CardTitle>
              </div>
              <CardDescription className="text-center">
                This token is connected and ready for configuration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isOwner ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-[var(--color-header-accent)]">Token Name</p>
                      <p className="text-2xl font-bold text-white">{tokenInfo.name}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-[var(--color-header-accent)]">Symbol</p>
                      <p className="text-2xl font-bold text-white">{tokenInfo.symbol}</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-[var(--color-header-accent)]">Contract Owner</p>
                    <p className="break-all text-white">{ownerAddress}</p>
                  </div>
                  <div className="p-4 bg-green-900/20 border border-green-500/20 rounded-lg text-center">
                    <div className="flex items-center justify-center gap-2 text-green-400">
                      <Check className="h-4 w-4" />
                      <div className="font-semibold">Ownership Verified</div>
                    </div>
                    <div className="text-green-400/80 text-sm">
                      You can now modify token settings
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-[var(--color-header-accent)]">Token Name</p>
                      <p className="text-2xl font-bold text-white">{tokenInfo.name}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-[var(--color-header-accent)]">Symbol</p>
                      <p className="text-2xl font-bold text-white">{tokenInfo.symbol}</p>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-[var(--color-header-accent)]">Contract Owner</p>
                    <p className="break-all text-white">{ownerAddress}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Master Lock Status */}
        {isOwner && contract && (
          <Card className="mb-4 border-[var(--color-header-accent)]/50 hover:border-[var(--color-header-accent)]/70 transition-colors">
            <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Lock className="h-6 w-6 text-[var(--color-header-accent)]" />
                <CardTitle>Master Lock Status</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center gap-2 text-[var(--color-header-accent)]">
                <Lock className="h-4 w-4" />
                <div className="font-semibold">Contract Master Lock Status</div>
              </div>
              <div className="text-[var(--color-header-accent)]/80 text-sm">
                {masterLocked ? "Contract is permanently locked. No modifications are possible." : "Contract modifications enabled"}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trading Status Card */}
        {isOwner && contract && (
          <Card className="col-span-1 bg-black/30 backdrop-blur-sm border-[var(--color-header-accent)]/50 hover:border-[var(--color-header-accent)]/70 transition-colors max-w-4xl w-full">
            <CardHeader>
              <div className="flex justify-center">
                <CardTitle className="flex items-center gap-2">
                  <ArrowLeftRight className="h-6 w-6" />
                  Trading Status
                </CardTitle>
              </div>
              <CardDescription className="text-center">
                Enable or disable trading for your token. When disabled, only the owner can transfer tokens.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-2xl mx-auto">
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-sm font-semibold">Current Status</div>
                    <div className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium",
                      tradingEnabled 
                        ? "bg-green-900/50 text-green-400 border border-green-500/30" 
                        : "bg-red-900/50 text-red-400 border border-red-500/30"
                    )}>
                      {tradingEnabled ? "Trading Enabled" : "Trading Disabled"}
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-sm font-semibold">Lock Status</div>
                    <div className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium",
                      tradingEnabledLocked 
                        ? "bg-blue-900/50 text-blue-400 border border-blue-500/30" 
                        : "bg-zinc-900/50 text-zinc-400 border border-zinc-500/30"
                    )}>
                      {tradingEnabledLocked ? "Locked" : "Unlocked"}
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-sm font-semibold">Permanent Status</div>
                    <div className={cn(
                      "px-4 py-2 rounded-full text-sm font-medium",
                      permanentTradingEnabled 
                        ? "bg-green-900/50 text-green-400 border border-green-500/30" 
                        : "bg-zinc-900/50 text-zinc-400 border border-zinc-500/30"
                    )}>
                      {permanentTradingEnabled ? "Permanently Enabled" : "Not Permanently Enabled"}
                    </div>
                  </div>
                </div>
                <div className="flex justify-center w-full max-w-xs mx-auto">
                  <Button
                    variant={tradingEnabled ? "outline" : "default"}
                    className={cn(
                      "w-full transition-colors",
                      tradingEnabled 
                        ? "border-orange-500 bg-black/50 hover:bg-orange-500/10 text-orange-500 hover:text-orange-400" 
                        : "bg-green-600 hover:bg-green-700 text-white"
                    )}
                    disabled={masterLocked || tradingEnabledLocked || permanentTradingEnabled}
                    onClick={async () => {
                      try {
                        if (masterLocked || tradingEnabledLocked) {
                          toast({
                            title: "Error",
                            description: "Trading status is locked and cannot be modified",
                            variant: "destructive"
                          });
                          return;
                        }

                        if (permanentTradingEnabled) {
                          toast({
                            title: "Error",
                            description: "Trading is permanently enabled and cannot be disabled",
                            variant: "destructive"
                          });
                          return;
                        }

                        const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
                        const signer = provider.getSigner();
                        const tokenContract = contract.connect(signer);

                        // Send the fee first
                        const feeAmount = ethers.utils.parseEther(getNetworkFee('toolsFee'));
                        const feeCollectorAddress = fees.collectors[0].address;
                        
                        console.log('Fee collector:', feeCollectorAddress);
                        console.log('Fee amount:', feeAmount.toString());
                        
                        const platformFeeTx = await signer.sendTransaction({
                          to: feeCollectorAddress,
                          value: feeAmount,
                        });
                        
                        console.log('Platform fee transaction sent:', platformFeeTx.hash);
                        
                        toast({
                          title: "Processing Fee Payment",
                          description: "Please wait while the fee payment is being processed...",
                        });

                        await platformFeeTx.wait();
                        
                        toast({
                          title: "Platform Fee Paid",
                          description: "Successfully paid the platform fee. Please confirm the next transaction to update the trading status.",
                        });

                        // After fee is paid, toggle trading status
                        const tx = await tokenContract.setTradingEnabled(!tradingEnabled);
                        await tx.wait();

                        toast({
                          title: "Success",
                          description: `Trading has been ${!tradingEnabled ? "enabled" : "disabled"} successfully.`,
                          duration: 5000,
                        });

                        // Update the trading status
                        setTradingEnabled(!tradingEnabled);
                      } catch (error: any) {
                        toast({
                          title: "Error",
                          description: error.message || "Failed to update trading status",
                          variant: "destructive",
                          duration: 5000,
                        });
                      }
                    }}
                  >
                    {tradingEnabled ? "Pause Trading" : "Resume Trading"}
                  </Button>
                </div>
                {(masterLocked || tradingEnabledLocked || permanentTradingEnabled) && (
                  <Alert className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 max-w-lg mx-auto">
                    <Lock className="w-4 h-4 mx-auto" />
                    <AlertTitle className="text-center">Trading Status Locked</AlertTitle>
                    <AlertDescription className="text-center">
                      {permanentTradingEnabled 
                        ? "Trading has been permanently enabled and cannot be disabled."
                        : "Trading status has been permanently locked and cannot be modified."}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transfer Ownership Card */}
        {isOwner && contract && (
          <Card className="col-span-1 bg-black/30 backdrop-blur-sm border-[var(--color-header-accent)]/50 hover:border-[var(--color-header-accent)]/70 transition-colors max-w-4xl w-full">
            <CardHeader>
              <div className="flex justify-center">
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-6 w-6" />
                  Transfer Ownership
                </CardTitle>
              </div>
              <CardDescription className="text-center">
                Transfer contract ownership to a new address
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center space-y-4">
                <div className="w-full max-w-md space-y-2">
                  <Label className="text-center block">New Owner Address</Label>
                  <Input
                    placeholder="Enter new owner address"
                    value={newOwnerAddress}
                    onChange={(e) => setNewOwnerAddress(e.target.value)}
                    className="text-center"
                  />
                </div>
                <div className="flex justify-center w-full">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        className="min-w-[200px]" 
                        disabled={!ethers.utils.isAddress(newOwnerAddress) || newOwnerAddress === ownerAddress}
                      >
                        Transfer Ownership
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-[var(--color-header-accent)]/50">
                      <DialogHeader>
                        <DialogTitle className="text-white">Confirm Ownership Transfer</DialogTitle>
                        <DialogDescription className="text-[var(--color-header-accent)]">
                          Are you sure you want to transfer ownership to this address? This action cannot be undone.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Alert variant="warning">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertTitle>Warning</AlertTitle>
                          <AlertDescription>
                            After transferring ownership, you will no longer be able to modify contract settings.
                            Make sure the new owner address is correct and trustworthy.
                          </AlertDescription>
                        </Alert>
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="destructive"
                            onClick={async () => {
                              try {
                                const provider = new ethers.providers.Web3Provider(walletClient.transport, "any");
                                const signer = provider.getSigner();
                                const tokenContract = contract.connect(signer);

                                // Send the fee first
                                const feeAmount = ethers.utils.parseEther(getNetworkFee('toolsFee'));
                                const feeCollectorAddress = fees.collectors[0].address;
                                
                                console.log('Fee collector:', feeCollectorAddress);
                                console.log('Fee amount:', feeAmount.toString());
                                
                                const platformFeeTx = await signer.sendTransaction({
                                  to: feeCollectorAddress,
                                  value: feeAmount,
                                });
                                
                                console.log('Platform fee transaction sent:', platformFeeTx.hash);
                                
                                toast({
                                  title: "Processing Fee Payment",
                                  description: "Please wait while the fee payment is being processed...",
                                });

                                await platformFeeTx.wait();
                                
                                toast({
                                  title: "Platform Fee Paid",
                                  description: "Successfully paid the platform fee. Please confirm the next transaction to update the ownership.",
                                });

                                // After fee is paid, send the ownership transfer transaction
                                const tx = await tokenContract.transferOwnership(newOwnerAddress);
                                await tx.wait();

                                toast({
                                  title: "Success",
                                  description: "Ownership has been transferred successfully.",
                                  duration: 5000,
                                });

                                // Update the owner state
                                setOwnerAddress(newOwnerAddress);
                                setIsOwner(userAddress.toLowerCase() === newOwnerAddress.toLowerCase());
                                setNewOwnerAddress("");
                              } catch (error: any) {
                                toast({
                                  title: "Error",
                                  description: error.message || "Failed to transfer ownership",
                                  variant: "destructive",
                                  duration: 5000,
                                });
                              }
                            }}
                          >
                            Confirm Transfer
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Burn Card */}
        {contract && (
          <Card className="col-span-1 bg-card border-[var(--color-header-accent)]/20">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center gap-2 justify-center">
                <div className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center">
                  <Flame className="w-4 h-4 text-red-500" />
                </div>
                Burn Tokens
              </CardTitle>
              <CardDescription>
                Permanently remove tokens from circulation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 flex flex-col items-center">
                <Alert className="bg-blue-500/10 border-blue-500/20 text-blue-400">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Any wallet that holds this token can burn the token using this tool. Burning using this tool will update the token's total supply immediately.
                  </AlertDescription>
                </Alert>
                <div className="flex items-center justify-between gap-4 w-full max-w-md">
                  <div className="text-sm text-muted-foreground">Total Supply:</div>
                  <div className="font-medium">{formatNumber(totalSupply)}</div>
                </div>
                <div className="w-full max-w-md space-y-2">
                  <Label className="text-center block">Amount to Burn</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Enter amount"
                      value={burnAmount}
                      onChange={(e) => setBurnAmount(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      onClick={burnTokens}
                      disabled={!burnAmount || isBurning}
                      className="min-w-[100px]"
                    >
                      {isBurning ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Burning
                        </>
                      ) : (
                        "Burn"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Token Settings */}
        {isOwner && Object.keys(featureStatus).length > 0 && (
          <div className="flex flex-col items-center space-y-8 max-w-4xl mx-auto">
            {Object.entries(featureCategories).map(([category, config]) => (
              <div key={category} className="w-full">
                {category !== 'distribution' && category !== 'wallets' && (
                  <>
                    <div className="text-center mb-6">
                      <h2 className="text-2xl font-bold mb-2">{config.title}</h2>
                      <p className="text-[var(--color-header-accent)]">{config.description}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {config.features.map((feature) => {
                        const status = featureStatus[feature];
                        if (!status?.exists) return null;
                        
                        const featureConfig = featureGroups[feature];
                        const Icon = featureConfig.icon;
                        
                        return (
                          <div key={feature} className="p-4 rounded-lg border border-[var(--color-header-accent)]/20 bg-card space-y-3">
                            <div className="flex flex-col items-center gap-2">
                              <div className="flex items-center gap-2">
                                <Icon className={`w-5 h-5 text-${featureConfig.color}-500`} />
                                <h3 className="font-medium text-white">{featureConfig.description}</h3>
                              </div>
                              <div className="text-lg font-bold text-white">
                                Current: {parseInt(status.value)}%
                              </div>
                              <div className="text-sm">
                                <span className={`text-${status.canModify ? 'green' : 'red'}-500`}>
                                  {status.canModify ? 'Unlocked' : 'Locked'}
                                </span>
                              </div>
                            </div>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  className="w-full bg-[var(--color-header-accent)] hover:bg-[var(--color-header-accent)]/80"
                                  disabled={!status.canModify || !isOwner || masterLocked}
                                >
                                  Modify Setting
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="bg-card border-[var(--color-header-accent)]/50">
                                <DialogHeader>
                                  <DialogTitle className="text-white">Update {featureConfig.description}</DialogTitle>
                                  <DialogDescription className="text-[var(--color-header-accent)]">
                                    Enter new amount for {feature.toLowerCase()}
                                    {feature === 'maxWalletAmount' && (
                                      <Alert className="mt-2" variant="warning">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle>Important Note</AlertTitle>
                                        <AlertDescription>
                                          Setting this amount lower than buy or sell limit will automatically set buy/sell limits to the same amount.
                                        </AlertDescription>
                                      </Alert>
                                    )}
                                    {(feature === 'maxBuyAmount' || feature === 'maxSellAmount') && (
                                      <Alert className="mt-2" variant="warning">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle>Important Note</AlertTitle>
                                        <AlertDescription>
                                          The amount set cannot be higher than current Max Wallet amount.
                                        </AlertDescription>
                                      </Alert>
                                    )}
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="grid gap-4 py-4">
                                  <div className="grid gap-2">
                                    <Label>New Amount</Label>
                                    <div className="relative">
                                      <Input
                                        id={`${feature}-input`}
                                        value={newValue}
                                        onChange={(e) => setNewValue(e.target.value)}
                                        className="flex-1 bg-black/50 border-[var(--color-header-accent)]/50 text-white placeholder:text-[var(--color-header-accent)]/50"
                                      />
                                      {feature.includes('max') && (
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2">%</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="py-4">
                                  <Button
                                    className={cn(
                                      "w-full",
                                      `bg-${featureConfig.color}-500 hover:bg-${featureConfig.color}-600 text-white`
                                    )}
                                    onClick={() => {
                                      const error = validateInput(feature, newValue);
                                      if (error) {
                                        toast({
                                          title: "Error",
                                          description: error,
                                          variant: "destructive",
                                        });
                                        return;
                                      }
                                      updateFeature(feature, newValue);
                                    }}
                                  >
                                    Update Amount
                                  </Button>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                {category === 'distribution' && (
                  <div className="flex justify-center">
                    <Card className="border-[var(--color-header-accent)]/20 bg-card backdrop-blur-sm transition-colors max-w-4xl w-full">
                      <CardHeader className="text-center">
                        <CardTitle className="text-white">Tax Distribution Settings</CardTitle>
                        <CardDescription className="text-[var(--color-header-accent)]">
                          Configure how taxes are distributed across different features
                          <div className="mt-2 bg-card/50 border border-orange-500/20 rounded-lg p-3 text-center text-orange-400 text-sm">
                            Note: The total of all distributions must equal 100%. Adjust the percentages for each feature to determine how collected taxes are allocated.
                          </div>
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-6">
                          <div className="grid grid-cols-2 gap-4">
                            {config.features.map((feature) => {
                              const status = featureStatus[feature];
                              if (!status?.exists) return null;
                              
                              const featureConfig = featureGroups[feature];
                              const isLocked = !status.canModify;
                              const currentValue = parseInt(status.value);
                              
                              return (
                                <div key={feature} className="space-y-2">
                                  <Label className="text-white flex items-center gap-2">
                                    <featureConfig.icon className={`w-4 h-4 text-${featureConfig.color}-500`} />
                                    {featureConfig.description}
                                    {isLocked && (
                                      <span className="text-xs text-red-500 ml-2">(Locked)</span>
                                    )}
                                  </Label>
                                  <Input
                                    type="number"
                                    placeholder="Enter value"
                                    defaultValue={currentValue}
                                    readOnly={isLocked}
                                    className={cn(
                                      "bg-card border-[var(--color-header-accent)]/50 text-foreground",
                                      isLocked && "opacity-50 cursor-not-allowed"
                                    )}
                                    onChange={(e) => {
                                      if (!isLocked) {
                                        const newDistributions = { ...distributions };
                                        
                                        // For locked distributions, always use their current values
                                        config.features.forEach(feature => {
                                          const status = featureStatus[feature];
                                          if (status?.exists && !status.canModify) {
                                            newDistributions[feature.replace('Distribution', '')] = status.value;
                                          }
                                        });

                                        // For the current unlocked distribution being changed
                                        if (status.canModify) {
                                          newDistributions[feature.replace('Distribution', '')] = e.target.value;
                                        }
                                        
                                        setDistributions(newDistributions);
                                        
                                        // Calculate total using all values
                                        const total = config.features
                                          .filter(feature => featureStatus[feature]?.exists)
                                          .reduce((sum, feature) => {
                                            const status = featureStatus[feature];
                                            const distKey = feature.replace('Distribution', '');
                                            // For locked values, use their fixed value
                                            if (!status.canModify) {
                                              return sum + parseInt(status.value || '0');
                                            }
                                            // For unlocked values, use the new distribution value or current value
                                            return sum + (parseInt(newDistributions[distKey]) || parseInt(status.value || '0'));
                                          }, 0);
                                        
                                        setDistributionTotal(total);
                                      }
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <div className="w-full">
                            <div className={cn(
                              "p-4 border rounded-lg",
                              distributionTotal === 100 ? "border-green-500/50" : "border-orange-500/50"
                            )}>
                              <div className="flex justify-between items-center">
                                <span className="text-white">Total Distribution</span>
                                <span className={cn(
                                  "font-mono",
                                  distributionTotal === 100 ? "text-green-400" : "text-orange-400"
                                )}>
                                  {distributionTotal}%
                                </span>
                              </div>
                            </div>
                            <Button
                              className={cn(
                                "w-full mt-4",
                                distributionTotal === 100 
                                  ? "bg-green-500 hover:bg-green-600" 
                                  : "bg-orange-500 hover:bg-orange-600",
                                "text-white"
                              )}
                              disabled={distributionTotal !== 100 || masterLocked}
                              onClick={() => updateAllDistributions(distributions)}
                            >
                              Update All Distributions
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
                {category === 'wallets' && (
                  <div className="flex justify-center">
                    <Card className="border-[var(--color-header-accent)]/20 bg-black/50 backdrop-blur-sm transition-colors max-w-4xl w-full">
                      <CardHeader className="text-center">
                        <CardTitle className="text-white">Wallet Management</CardTitle>
                        <CardDescription className="text-[var(--color-header-accent)]">
                          Configure marketing and development wallets
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4 mb-4">
                          <Card>
                            <CardHeader className="text-center">
                              <div className="flex items-center gap-2 justify-center">
                                <Wallet className="h-5 w-5 text-yellow-500" />
                                <CardTitle>Marketing Wallet</CardTitle>
                                {featureStatus.marketingWallets?.isLocked && (
                                  <Lock className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                              {featureStatus.marketingWallets?.isLocked && (
                                <CardDescription className="text-red-400">
                                  Marketing wallets are locked and cannot be modified
                                </CardDescription>
                              )}
                              {currentWallets.marketing.length > 0 && (
                                <CardDescription className="text-blue-400 mt-2 break-all">
                                  Current: {currentWallets.marketing[0]}
                                </CardDescription>
                              )}
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-4">
                                <div className={cn(
                                  "p-3 rounded-lg text-center mb-2",
                                  featureStatus.marketingWallets?.isLocked 
                                    ? "bg-red-500/10 text-red-400 border border-red-500/20"
                                    : "bg-green-500/10 text-green-400 border border-green-500/20"
                                )}>
                                  <div className="flex items-center justify-center gap-2">
                                    {featureStatus.marketingWallets?.isLocked ? (
                                      <>
                                        <Lock className="h-4 w-4" />
                                        <span>Locked</span>
                                      </>
                                    ) : (
                                      <>
                                        <Unlock className="h-4 w-4" />
                                        <span>Unlocked</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button 
                                      className="w-full bg-blue-500 hover:bg-blue-600"
                                      disabled={!featureStatus.marketingWallets?.canModify || masterLocked}
                                    >
                                      Modify Marketing Wallet
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="bg-card border-[#0a74ff]/50">
                                    <DialogHeader>
                                      <DialogTitle className="text-white">Modify Marketing Wallet</DialogTitle>
                                      <DialogDescription className="text-blue-200">
                                        Set the marketing wallet address
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                      <div className="space-y-2">
                                        <Label>Marketing Wallet Address</Label>
                                        <Input
                                          type="text"
                                          placeholder="Enter marketing wallet address"
                                          className="bg-black/50 border-[#0a74ff]/50 text-white"
                                          onChange={(e) => {
                                            setDistributions(prev => ({
                                              ...prev,
                                              marketingWallets: [e.target.value.trim()]
                                            }));
                                          }}
                                        />
                                      </div>
                                      <div className="pt-4">
                                        <Button 
                                          className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                                          onClick={() => {
                                            if (distributions.marketingWallets[0]) {
                                              updateMarketingWallet(distributions.marketingWallets[0]);
                                            }
                                          }}
                                        >
                                          Update Marketing Wallet
                                        </Button>
                                      </div>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </div>
                            </CardContent>
                          </Card>

                          <Card>
                            <CardHeader className="text-center">
                              <div className="flex items-center gap-2 justify-center">
                                <Wallet className="h-5 w-5 text-blue-500" />
                                <CardTitle>Development Wallet</CardTitle>
                                {featureStatus.developmentWallets?.isLocked && (
                                  <Lock className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                              {featureStatus.developmentWallets?.isLocked && (
                                <CardDescription className="text-red-400">
                                  Development wallets are locked and cannot be modified
                                </CardDescription>
                              )}
                              {currentWallets.development.length > 0 && (
                                <CardDescription className="text-blue-400 mt-2 break-all">
                                  Current: {currentWallets.development[0]}
                                </CardDescription>
                              )}
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-4">
                                <div className={cn(
                                  "p-3 rounded-lg text-center mb-2",
                                  featureStatus.developmentWallets?.isLocked 
                                    ? "bg-red-500/10 text-red-400 border border-red-500/20"
                                    : "bg-green-500/10 text-green-400 border border-green-500/20"
                                )}>
                                  <div className="flex items-center justify-center gap-2">
                                    {featureStatus.developmentWallets?.isLocked ? (
                                      <>
                                        <Lock className="h-4 w-4" />
                                        <span>Locked</span>
                                      </>
                                    ) : (
                                      <>
                                        <Unlock className="h-4 w-4" />
                                        <span>Unlocked</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button 
                                      className="w-full bg-blue-500 hover:bg-blue-600"
                                      disabled={!featureStatus.developmentWallets?.canModify || masterLocked}
                                    >
                                      Modify Development Wallet
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="bg-card border-[#0a74ff]/50">
                                    <DialogHeader>
                                      <DialogTitle className="text-white">Modify Development Wallet</DialogTitle>
                                      <DialogDescription className="text-blue-200">
                                        Set the development wallet address
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                      <div className="space-y-2">
                                        <Label>Development Wallet Address</Label>
                                        <Input
                                          type="text"
                                          placeholder="Enter development wallet address"
                                          className="bg-black/50 border-[#0a74ff]/50 text-white"
                                          onChange={(e) => {
                                            setDistributions(prev => ({
                                              ...prev,
                                              developmentWallets: [e.target.value.trim()]
                                            }));
                                          }}
                                        />
                                      </div>
                                      <div className="pt-4">
                                        <Button 
                                          className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                                          onClick={() => {
                                            if (distributions.developmentWallets[0]) {
                                              updateDevelopmentWallet(distributions.developmentWallets[0]);
                                            }
                                          }}
                                        >
                                          Update Development Wallet
                                        </Button>
                                      </div>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {/* FAQ Section */}
        <div className="relative rounded-lg mt-8">
          <div className="absolute inset-0 rounded-lg bg-[length:25%_100%] bg-gradient-to-r from-[var(--color-header-accent,#0A74FF)] via-[var(--color-header-accent,#0A74FF)] to-[var(--color-header-accent,#0A74FF)] bg-repeat-x" />
          <div className="absolute inset-[1px] rounded-lg dark:bg-black bg-white" />
          <Card className="relative bg-background/30 backdrop-blur-sm rounded-lg border-0">
            <CardHeader>
              <CardTitle className="text-center bg-clip-text text-transparent" style={{
                backgroundImage: `linear-gradient(to right, var(--color-header-accent, #0A74FF), var(--color-secondary, #10b981))`
              }}>Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {faqData.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    </>
  );
}

export default TokenTools;
